<template>
  <SmDialog
    v-model:visibility="visibility"
    :title="i18n.t(`optimizationNames.${props.optimization.type}`)"
    size="medium">
    <!-- Description -->
    <slot name="description">
      <i18n-t
        :keypath="getDescription()"
        tag="p"
        class="flex flex-wrap items-center gap-2 break-normal">
        <!-- User Name-->
        <template v-if="getPersonName()" #name>
          <SmTag
            :label="getPersonName()"
            :icon="User"
            class="cursor-pointer transition hover:bg-gray-300 dark:hover:bg-gray-500"
            :hide-tooltip="true"
            @click="openUserDetailsDrawer(props.optimization.person_id)" />
        </template>

        <template #application>
          <SmTag
            :label="props.optimization.application_display_name"
            :icon="LayoutGrid"
            class="cursor-pointer transition hover:bg-gray-300 dark:hover:bg-gray-500"
            :hide-tooltip="true"
            @click="openApplicationPage()" />
        </template>

        <template
          v-if="
            props.optimization.type === OptimizationType.SOFTWARE_ACCOUNT_UNUSED
          "
          #days>
          {{ getDays() }}
        </template>

        <template
          v-if="
            props.optimization.type ===
              OptimizationType.SOFTWARE_ACCOUNT_REDUNDANT_LICENSES ||
            props.optimization.type ===
              OptimizationType.SOFTWARE_ACCOUNT_DUPLICATE
          "
          #count>
          {{ getCount() }}
        </template>
      </i18n-t>

      <p v-if="props.optimization.savings" class="mt-2">
        {{
          i18n.t('optimizationSavingsDescription', {
            savings: saving,
          })
        }}
      </p>
    </slot>

    <div class="h-8"></div>

    <slot></slot>

    <template #footer>
      <slot name="footer"> </slot>
    </template>
  </SmDialog>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import { EnrichedOptimizations } from '@/stores/optimization/optimization.store'
  import {
    OptimizationSoftwareAccountDuplicateData,
    OptimizationSoftwareAccountRedundantLicenseData,
    OptimizationSoftwareAccountUnusedData,
    OptimizationType,
  } from '@/client'
  import router from '@/router'
  import dayjs from 'dayjs'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { User, LayoutGrid } from 'lucide-vue-next'

  const i18n = useI18n()

  const props = defineProps({
    optimization: {
      type: Object as PropType<EnrichedOptimizations>,
      required: true,
    },
  })

  const visibility = defineModel({
    type: Boolean,
    required: true,
  })

  const emit = defineEmits(['openPersonDrawer'])

  // ----------------------------------------------------------------------
  // Get description
  // ----------------------------------------------------------------------

  function getDescription() {
    switch (props.optimization.type) {
      case OptimizationType.EMPLOYEE_LEAVING:
        return 'optimizationDescription.employeeLeaving'
      case OptimizationType.SOFTWARE_ACCOUNT_DUPLICATE: {
        return 'optimizationDescription.softwareAcountDuplicate'
      }
      case OptimizationType.SOFTWARE_ACCOUNT_REDUNDANT_LICENSES: {
        return 'optimizationDescription.softwareAccountRedundantLicenses'
      }
      case OptimizationType.SOFTWARE_ACCOUNT_UNUSED: {
        return hasNeverLoggedIn()
          ? 'optimizationDescription.softwareAccountUnusedNeverLoggedIn'
          : 'optimizationDescription.softwareAccountUnused'
      }
      case OptimizationType.SOFTWARE_ACCOUNT_RIGHTSIZING: {
        return 'optimizationDescription.softwareAccountRightsizing'
      }
      default:
        return ''
    }
  }

  function getPersonName(): string {
    if (props.optimization.person_name) {
      return props.optimization.person_name
    } else if (
      props.optimization.person_first_name &&
      props.optimization.person_last_name
    ) {
      return `${props.optimization.person_first_name} ${props.optimization.person_last_name}`
    } else if (props.optimization.person_email) {
      return props.optimization.person_email
    } else {
      return props.optimization.person_id
    }
  }

  function hasNeverLoggedIn() {
    const info = props.optimization
      .data as OptimizationSoftwareAccountUnusedData
    return info.never_logged_in
  }

  function getDays() {
    const info = props.optimization
      .data as OptimizationSoftwareAccountUnusedData
    const daysSince = dayjs().diff(dayjs(info?.last_login_time), 'day')

    return daysSince
  }

  function getCount() {
    if (
      props.optimization.type === OptimizationType.SOFTWARE_ACCOUNT_DUPLICATE
    ) {
      const info = props.optimization
        .data as OptimizationSoftwareAccountDuplicateData
      return info?.software_account_ids.length
    } else if (
      props.optimization.type ===
      OptimizationType.SOFTWARE_ACCOUNT_REDUNDANT_LICENSES
    ) {
      const info = props.optimization
        .data as OptimizationSoftwareAccountRedundantLicenseData

      const totalLength = new Set(
        info.redundant_software_licenses.flatMap(
          (item) => item.redundant_software_license_ids
        )
      ).size

      return totalLength
    }
  }

  const saving = computed(() => {
    return getCorrectedPrice(props.optimization.savings * 12)
  })

  // ----------------------------------------------------------------------
  // Handle tag click events
  // ----------------------------------------------------------------------

  function openUserDetailsDrawer(id: string) {
    emit('openPersonDrawer', id)
    visibility.value = false
  }

  function openApplicationPage() {
    router.push({
      name: 'Application',
      params: { id: props.optimization.application_id },
    })
  }
</script>
